"use client";

import type { Media } from "@mw/shared/payload-types";
import type { AspectRatioKey, AspectRatioRounded } from "@ui/components/core/AspectRatio";

import { Image } from "@/app/_ui/components/core/Image";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useMemo } from "react";

export default function BackgroundForegroundImage({
  animationKey,
  background,
  className,
  foreground,
  mobileForeground,
  overlay,
  theme = "light",
  aspectRatio = "1x1",
  disableScaleAnimation = false,
  minimumOpacity = 0,
  rounded = "lg",
  imageSize = ImageSizes.FULL_HALF_ON_LG,
  children,
}: {
  animationKey?: number | string;
  aspectRatio?: AspectRatioKey;
  background?: Media;
  className?: string;
  disableScaleAnimation?: boolean;
  foreground: Media;
  imageSize?: (typeof ImageSizes)[keyof typeof ImageSizes];
  minimumOpacity?: number;
  mobileForeground?: Media;
  overlay?: Media;
  rounded?: AspectRatioRounded;
  theme?: "dark" | "light" | "transparent";
  children?: React.ReactNode;
}) {
  const backgroundColor = useMemo(() => {
    return theme === "dark" ? "#1E1916" : theme === "transparent" ? "transparent" : "#F7F4F2";
  }, [theme]);

  const overlayOpacity = useMemo(() => {
    if (background) return 0.28; // With background image
    return theme === "dark" ? 0.12 : 0.08; // Without background image
  }, [background, theme]);

  return (
    <AspectRatio className={cn("relative", className)} ratio={aspectRatio} rounded={rounded}>
      {/* Background image crossfade or solid color */}
      <AnimatePresence mode='popLayout'>
        <m.div
          animate={{ opacity: 1, scale: disableScaleAnimation ? 1 : 1.05 }}
          className='absolute inset-0 z-0 h-full w-full'
          exit={{ opacity: minimumOpacity, scale: 1.05, transition: { duration: 0.3, delay: 0.3, ease: "easeInOut" } }}
          initial={{ opacity: minimumOpacity, scale: disableScaleAnimation ? 1 : 1.05 }}
          key={animationKey}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Theme color as base background */}
          <div className='h-full w-full rounded-lg' style={{ backgroundColor }} />
          {/* Background image overlays on theme color */}
          {background && (
            <Image
              alt='Background'
              className='absolute inset-0 h-full w-full'
              fit='cover'
              quality={100}
              resource={background}
              sizes={imageSize}
            />
          )}
        </m.div>
      </AnimatePresence>

      {/* Optional overlay layer - animates with background */}
      {overlay && (
        <AnimatePresence mode='popLayout'>
          <m.div
            animate={{
              opacity: overlayOpacity,
              scale: disableScaleAnimation ? 1 : 1.05,
            }}
            className='absolute inset-0 z-5 h-full w-full'
            exit={{ opacity: minimumOpacity, scale: 1.05, transition: { duration: 0.3, ease: "easeInOut" } }}
            initial={{ opacity: minimumOpacity, scale: disableScaleAnimation ? 1 : 0.95 }}
            key={`${animationKey}-overlay`}
            style={{ mixBlendMode: background ? "overlay" : "normal" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Image
              alt='Overlay'
              className='h-full w-full'
              fit='contain'
              quality={100}
              resource={overlay}
              sizes={imageSize}
            />
          </m.div>
        </AnimatePresence>
      )}

      {/* Foreground image slide/fade with direction */}
      <AnimatePresence mode='wait'>
        <m.div
          animate={{
            opacity: 1,
            scale: disableScaleAnimation ? 1 : 1.05,
          }}
          className='absolute inset-0 z-10'
          initial={{ opacity: 0.5, scale: disableScaleAnimation ? 1 : 1.05 }}
          key={animationKey}
          transition={{
            opacity: { duration: 0.4, type: "spring", stiffness: 320, damping: 32 },
            scale: { duration: 0.4, type: "spring", stiffness: 250, damping: 22 },
          }}
        >
          <Image
            alt='Foreground'
            className={cn("h-full w-full", mobileForeground && "hidden lg:block")}
            fit='contain'
            quality={100}
            resource={foreground}
            sizes={imageSize}
          />
          {mobileForeground && (
            <Image
              alt='Mobile Foreground'
              className='block h-full w-full lg:hidden'
              fit='contain'
              quality={100}
              resource={mobileForeground}
              sizes={imageSize}
            />
          )}
        </m.div>
      </AnimatePresence>
      {children}
    </AspectRatio>
  );
}
